import type { StockMovementType } from '@horevia/contracts';
import {
  Badge,
  Button,
  Card,
  CardBody,
  ChipFilter,
  Container,
  EmptyState,
  ErrorState,
  NoPermissionState,
  PageHeader,
  Table,
  TableSkeleton,
  TableWrap,
  Td,
  Th,
  Toolbar,
  Tr,
} from '@horevia/ui';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { History } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSession } from '@/features/auth/session';
import {
  STOCK_MOVEMENT_TYPES,
  movementTone,
  quantityLabel,
  signedQuantityLabel,
} from '@/features/stock/labels';
import { stockMovementsQueryOptions } from '@/features/stock/queries';
import { errorMessage, errorTraceId } from '@/lib/errors';
import { formatDateTime } from '@/lib/format';
import { useCurrentProperty } from '@/lib/property';

export const Route = createFileRoute('/_app/stock/movements')({ component: StockMovementsPage });

/** Journal immuable des mouvements (R-INV-01) : type, article, lieu, quantité, motif, auteur. */
function StockMovementsPage() {
  const { t, i18n } = useTranslation(['stock', 'common', 'errors']);
  const session = useSession();
  const { propertyId } = useCurrentProperty();
  const canRead = session.can('stock.read');
  const [type, setType] = useState<StockMovementType | undefined>(undefined);
  const query = useInfiniteQuery(stockMovementsQueryOptions({ propertyId, type }, 25, canRead));
  const rows = useMemo(() => query.data?.pages.flatMap((page) => page.data) ?? [], [query.data]);

  if (!canRead) {
    return (
      <Container>
        <NoPermissionState
          title={t('common:states.noPermissionTitle')}
          description={t('common:states.noPermissionDescription')}
        />
      </Container>
    );
  }

  return (
    <Container>
      <PageHeader
        title={t('stock:movements.title')}
        subtitle={t('stock:movements.subtitle')}
        breadcrumb={[
          { label: t('common:appName'), link: <Link to="/">{t('common:appName')}</Link> },
          { label: t('stock:title'), link: <Link to="/stock">{t('stock:title')}</Link> },
          { label: t('stock:movements.title') },
        ]}
      />
      <Card>
        <CardBody density="flush">
          <Toolbar>
            <div className="row row--wrap" style={{ gap: 6 }}>
              <ChipFilter active={type === undefined} onClick={() => setType(undefined)}>
                {t('stock:movements.allTypes')}
              </ChipFilter>
              {STOCK_MOVEMENT_TYPES.map((value) => (
                <ChipFilter key={value} active={type === value} onClick={() => setType(value)}>
                  {t(`stock:movementType.${value}`)}
                </ChipFilter>
              ))}
            </div>
          </Toolbar>
          {query.isPending ? (
            <TableSkeleton rows={8} columns={7} label={t('common:states.loading')} />
          ) : query.isError ? (
            <ErrorState
              title={t('common:states.errorTitle')}
              description={errorMessage(query.error, t)}
              traceId={errorTraceId(query.error)}
              retryLabel={t('common:actions.retry')}
              onRetry={() => void query.refetch()}
              traceCopyLabel={t('common:actions.copy')}
              traceCopiedLabel={t('common:actions.copied')}
            />
          ) : rows.length === 0 ? (
            <EmptyState
              icon={<History className="ico" aria-hidden="true" />}
              title={t('stock:movements.empty')}
              description={t('stock:movements.emptyHint')}
            />
          ) : (
            <>
              <TableWrap>
                <Table>
                  <thead>
                    <tr>
                      <Th>{t('stock:movements.columns.date')}</Th>
                      <Th>{t('stock:movements.columns.type')}</Th>
                      <Th>{t('stock:movements.columns.item')}</Th>
                      <Th>{t('stock:movements.columns.location')}</Th>
                      <Th align="right">{t('stock:movements.columns.quantity')}</Th>
                      <Th align="right">{t('stock:movements.columns.after')}</Th>
                      <Th>{t('stock:movements.columns.reason')}</Th>
                      <Th>{t('stock:movements.columns.author')}</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((movement) => (
                      <Tr key={movement.id}>
                        <Td>{formatDateTime(movement.occurredAt, i18n.language)}</Td>
                        <Td>
                          <Badge tone={movementTone(movement.type)}>
                            {t(`stock:movementType.${movement.type}`)}
                          </Badge>
                        </Td>
                        <Td>
                          <strong>{movement.itemName}</strong>{' '}
                          <span className="t-12 muted">{movement.itemCode}</span>
                        </Td>
                        <Td>{movement.locationName}</Td>
                        <Td numeric align="right">
                          {signedQuantityLabel(movement.quantity, i18n.language)}{' '}
                          <span className="t-12 muted">{t(`stock:unit.${movement.unit}`)}</span>
                        </Td>
                        <Td numeric align="right">
                          {quantityLabel(movement.quantityAfter, i18n.language)}
                        </Td>
                        <Td>
                          <div className="stack" style={{ gap: 2 }}>
                            {movement.reason ? <span>{movement.reason}</span> : null}
                            {movement.sourceReference ? (
                              <span className="t-12 muted mono">{movement.sourceReference}</span>
                            ) : null}
                          </div>
                        </Td>
                        <Td>{movement.createdByName ?? '—'}</Td>
                      </Tr>
                    ))}
                  </tbody>
                </Table>
              </TableWrap>
              {query.hasNextPage ? (
                <div className="row" style={{ justifyContent: 'center', padding: 12 }}>
                  <Button
                    variant="secondary"
                    loading={query.isFetchingNextPage}
                    onClick={() => void query.fetchNextPage()}
                  >
                    {t('common:actions.loadMore')}
                  </Button>
                </div>
              ) : null}
            </>
          )}
        </CardBody>
      </Card>
    </Container>
  );
}
